'use client';

import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'next/navigation';
import {
  User,
  Phone,
  Mail,
  Calendar,
  Clock,
  MapPin,
  Car,
  Users,
  UserCheck,
  FileText,
  ChevronDown,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';

const WA_NUMBER = '6287786556067';

const vehicleOptions = [
  'Alphard gen 4',
  'Alphard Facelift',
  'Sprinter',
  'Fortuner',
  'Pajero',
  'Ioniq 5',
  'BMW 528i',
  'Denza',
  'Innova zenix',
  'Innova Reborn',
  'Innova Venturer',
  'Neta V-II',
  'Wuling AirEV',
  'Binguo EV',
  'Xpander',
  'Avanza',
  'Xenia',
  'Jazz',
  'Brio',
  'Sigra/Calya',
  'Ayla/Agya',
  'Ertiga',
  'Raize',
  'Big Bus 50–59 Seat',
  'Medium Bus 31–35 Seat',
  'Elf long 19 Seat',
  'Hiace Premio 14 Seat',
  'Hiace Commuter 15 Seat',
  'Hilux DC',
  'Triton DC',
  'Double Engkel Box (CDD Box) 5.000 kg',
  'Engkel Box (CDE Box) 2.200 kg',
  'Box Kecil 1.000 kg',
  'Blind Van 720 kg',
];

type BookingFormData = {
  namaPemesan: string;
  namaTamu: string;
  nomorWhatsapp: string;
  email: string;
  jenisKendaraan: string;
  paket: string;
  jenisSewa: string;
  tanggalMulai: string;
  jamPenjemputan: string;
  tanggalSelesai: string;
  jamSelesai: string;
  lokasiPenjemputan: string;
  tujuan: string;
  lokasiFinish: string;
  jumlahPenumpang: string;
  rutePerjalanan: string;
  day1: string;
  day2: string;
  day3: string;
  catatan: string;
};

const getDateOnly = (dateString: string) => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const calculateDuration = (start: string, end: string): string => {
  if (!start || !end) return '-';

  const startDate = getDateOnly(start);
  const endDate = getDateOnly(end);

  const diffMs = endDate.getTime() - startDate.getTime();

  if (diffMs < 0) return '-';

  // Durasi dihitung berdasarkan jumlah tanggal, bukan jam.
  // Contoh: 23 Sep–23 Sep = 1 hari, 23 Sep–24 Sep = 2 hari.
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24)) + 1;

  return `${diffDays} hari`;
};

const formatDate = (dateString: string): string => {
  if (!dateString) return '-';

  return getDateOnly(dateString).toLocaleDateString('id-ID', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const formatTime = (timeString: string): string => {
  if (!timeString) return '-';
  return timeString;
};

export default function BookingFormSection() {
  const searchParams = useSearchParams();
  const preselectedVehicle = searchParams.get('vehicle') || '';

  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState<BookingFormData | null>(null);
  const [vehicleDropdownOpen, setVehicleDropdownOpen] = useState(false);
  const [vehicleSearch, setVehicleSearch] = useState('');

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<BookingFormData>({
    defaultValues: {
      jenisKendaraan: preselectedVehicle,
      jenisSewa: 'dengan-driver',
    },
  });

  useEffect(() => {
    if (preselectedVehicle) {
      setValue('jenisKendaraan', preselectedVehicle);
    }
  }, [preselectedVehicle, setValue]);

  const watchedValues = watch();

  const filteredVehicleOptions = vehicleOptions.filter((vehicle) =>
    vehicle.toLowerCase().includes(vehicleSearch.toLowerCase())
  );

  const onSubmit = (data: BookingFormData) => {
    setFormData(data);
    setSubmitted(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const buildWhatsAppMessage = (data: BookingFormData): string => {
    const duration = calculateDuration(data.tanggalMulai, data.tanggalSelesai);

    const jenisSewa =
      data.jenisSewa === 'dengan-driver'
        ? 'Dengan Driver'
        : 'Lepas Kunci';

    const msg = `Halo NAYVERA INDO TRANSPORT, saya ingin melakukan booking kendaraan.

Nama Pemesan: ${data.namaPemesan}
Nama Tamu: ${data.namaTamu || '-'}
Nomor WhatsApp: ${data.nomorWhatsapp}
Email: ${data.email || '-'}

Kendaraan yang diminati: ${data.jenisKendaraan}
Paket: ${data.paket || '-'}
Jenis Sewa: ${jenisSewa}

Tanggal Penggunaan: ${formatDate(data.tanggalMulai)}
Jam Penjemputan: ${formatTime(data.jamPenjemputan)}
Tanggal Selesai: ${formatDate(data.tanggalSelesai)}
Jam Selesai / Finish: ${formatTime(data.jamSelesai)}

Lokasi Penjemputan: ${data.lokasiPenjemputan}
Tujuan / Rute: ${data.tujuan}
Lokasi Finish: ${data.lokasiFinish || '-'}

Jumlah Penumpang: ${data.jumlahPenumpang} orang
Durasi Pemakaian: ${duration}

Rute Perjalanan: ${data.rutePerjalanan || '-'}
Day 1: ${data.day1 || '-'}
Day 2: ${data.day2 || '-'}
Day 3: ${data.day3 || '-'}

Catatan / Permintaan Khusus: ${data.catatan || '-'}

Mohon informasi ketersediaan dan harga. Terima kasih.`;

    return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(msg)}`;
  };

  if (submitted && formData) {
    const jenisSewa =
      formData.jenisSewa === 'dengan-driver'
        ? 'Dengan Driver'
        : 'Lepas Kunci';

    const summaryItems = [
      { label: 'Nama Pemesan', value: formData.namaPemesan },
      { label: 'Nama Tamu', value: formData.namaTamu || '-' },
      { label: 'No. WhatsApp', value: formData.nomorWhatsapp },
      { label: 'Email', value: formData.email || '-' },
      { label: 'Kendaraan', value: formData.jenisKendaraan },
      { label: 'Paket', value: formData.paket || '-' },
      { label: 'Jenis Sewa', value: jenisSewa },
      { label: 'Tanggal Penggunaan', value: formatDate(formData.tanggalMulai) },
      { label: 'Jam Penjemputan', value: formatTime(formData.jamPenjemputan) },
      { label: 'Tanggal Selesai', value: formatDate(formData.tanggalSelesai) },
      { label: 'Jam Selesai / Finish', value: formatTime(formData.jamSelesai) },
      { label: 'Lokasi Penjemputan', value: formData.lokasiPenjemputan },
      { label: 'Tujuan / Rute', value: formData.tujuan },
      { label: 'Lokasi Finish', value: formData.lokasiFinish || '-' },
      {
        label: 'Jumlah Penumpang',
        value: `${formData.jumlahPenumpang} orang`,
      },
      {
        label: 'Durasi Pemakaian',
        value: calculateDuration(
          formData.tanggalMulai,
          formData.tanggalSelesai
        ),
      },
      { label: 'Rute Perjalanan', value: formData.rutePerjalanan || '-' },
      { label: 'Day 1', value: formData.day1 || '-' },
      { label: 'Day 2', value: formData.day2 || '-' },
      { label: 'Day 3', value: formData.day3 || '-' },
    ];

    return (
      <section className="py-16 bg-background">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 size={40} className="text-green-600" />
              </div>

              <h2 className="text-2xl font-extrabold text-foreground mb-2">
                Booking Siap Dikirim!
              </h2>

              <p className="text-muted-foreground">
                Terima kasih, data booking Anda sudah kami siapkan.
                Silakan cek kembali detailnya sebelum dikirim ke WhatsApp.
              </p>
            </div>

            <div className="summary-card p-8 mb-6">
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-white/10">
                <div>
                  <div className="text-accent text-xs font-bold uppercase tracking-widest mb-1">
                    Ringkasan Booking
                  </div>
                  <div className="text-white font-extrabold text-xl">
                    NAYVERA INDO TRANSPORT
                  </div>
                </div>

                <div className="text-right">
                  <div className="text-white/50 text-xs">No. Referensi</div>
                  <div className="text-accent font-bold text-sm tabular-nums">
                    NIT-{Date.now().toString().slice(-6)}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {summaryItems.map((item) => (
                  <div key={`summary-${item.label}`}>
                    <div className="text-white/45 text-xs mb-0.5">
                      {item.label}
                    </div>
                    <div className="text-white font-semibold text-sm whitespace-pre-line">
                      {item.value}
                    </div>
                  </div>
                ))}

                {formData.catatan && (
                  <div className="sm:col-span-2">
                    <div className="text-white/45 text-xs mb-0.5">
                      Catatan / Permintaan Khusus
                    </div>
                    <div className="text-white font-semibold text-sm whitespace-pre-line">
                      {formData.catatan}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href={buildWhatsAppMessage(formData)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-3 bg-green-500 hover:bg-green-600 text-white py-4 rounded-xl font-bold text-base transition-all duration-200 hover:shadow-lg active:scale-95"
              >
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="white"
                >
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.198-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                KIRIM BOOKING VIA WHATSAPP
              </a>

              <button
                type="button"
                onClick={() => {
                  setSubmitted(false);
                  setFormData(null);
                }}
                className="btn-outline-gold px-8 py-4 rounded-xl font-bold text-base"
              >
                Ubah Booking
              </button>
            </div>

            <p className="text-center text-muted-foreground text-sm mt-4">
              Pesan WhatsApp sudah diformat otomatis. Klik tombol di atas
              untuk membuka WhatsApp.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-background">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 items-start">
          <div className="lg:col-span-2">
            <div className="bg-card rounded-2xl border border-border shadow-card p-8">
              <div className="mb-8">
                <h2 className="text-xl font-extrabold text-foreground mb-1">
                  Booking Kendaraan
                </h2>

                <p className="text-muted-foreground text-sm">
                  Silakan isi data perjalanan Anda. Tenang, isi yang Anda tahu
                  saja dan tim NAYVERA akan membantu melengkapinya.
                </p>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} noValidate>
                {/* SECTION 1 */}
                <div className="mb-8">
                  <div className="flex items-center gap-3 mb-5 pb-3 border-b border-border">
                    <div className="w-7 h-7 rounded-full bg-accent text-accent-foreground flex items-center justify-center text-xs font-extrabold">
                      1
                    </div>
                    <div>
                      <h3 className="font-bold text-foreground">
                        Data Pemesan
                      </h3>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Data utama untuk menghubungi Anda
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label
                        className="form-label-style"
                        htmlFor="namaPemesan"
                      >
                        Nama Pemesan <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <User
                          size={16}
                          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                        />
                        <input
                          id="namaPemesan"
                          type="text"
                          placeholder="Nama Anda"
                          className="form-input-style !pl-10"
                          {...register('namaPemesan', {
                            required: 'Nama pemesan wajib diisi',
                            minLength: {
                              value: 3,
                              message: 'Nama minimal 3 karakter',
                            },
                          })}
                        />
                      </div>
                      {errors.namaPemesan && (
                        <div className="flex items-center gap-1.5 mt-1">
                          <AlertCircle
                            size={12}
                            className="text-red-500"
                          />
                          <p className="form-error-style">
                            {errors.namaPemesan.message}
                          </p>
                        </div>
                      )}
                    </div>

                    <div>
                      <label
                        className="form-label-style"
                        htmlFor="namaTamu"
                      >
                        Nama Tamu
                      </label>
                      <p className="text-xs text-muted-foreground mb-1.5">
                        Opsional — jika kendaraan digunakan untuk tamu/orang lain
                      </p>
                      <div className="relative">
                        <User
                          size={16}
                          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                        />
                        <input
                          id="namaTamu"
                          type="text"
                          placeholder="Nama tamu / penumpang utama"
                          className="form-input-style !pl-10"
                          {...register('namaTamu')}
                        />
                      </div>
                    </div>

                    <div>
                      <label
                        className="form-label-style"
                        htmlFor="nomorWhatsapp"
                      >
                        Nomor WhatsApp <span className="text-red-500">*</span>
                      </label>
                      <p className="text-xs text-muted-foreground mb-1.5">
                        Nomor yang aktif untuk konfirmasi booking
                      </p>
                      <div className="relative">
                        <Phone
                          size={16}
                          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                        />
                        <input
                          id="nomorWhatsapp"
                          type="tel"
                          placeholder="08xx-xxxx-xxxx"
                          className="form-input-style !pl-10"
                          {...register('nomorWhatsapp', {
                            required: 'Nomor WhatsApp wajib diisi',
                            pattern: {
                              value: /^[0-9+\-\s]{10,16}$/,
                              message: 'Format nomor tidak valid',
                            },
                          })}
                        />
                      </div>
                      {errors.nomorWhatsapp && (
                        <div className="flex items-center gap-1.5 mt-1">
                          <AlertCircle
                            size={12}
                            className="text-red-500"
                          />
                          <p className="form-error-style">
                            {errors.nomorWhatsapp.message}
                          </p>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="form-label-style" htmlFor="email">
                        Email
                      </label>
                      <p className="text-xs text-muted-foreground mb-1.5">
                        Opsional — untuk konfirmasi tambahan
                      </p>
                      <div className="relative">
                        <Mail
                          size={16}
                          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                        />
                        <input
                          id="email"
                          type="email"
                          placeholder="nama@email.com"
                          className="form-input-style !pl-10"
                          {...register('email', {
                            pattern: {
                              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                              message: 'Format email tidak valid',
                            },
                          })}
                        />
                      </div>
                      {errors.email && (
                        <div className="flex items-center gap-1.5 mt-1">
                          <AlertCircle
                            size={12}
                            className="text-red-500"
                          />
                          <p className="form-error-style">
                            {errors.email.message}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* SECTION 2 */}
                <div className="mb-8">
                  <div className="flex items-center gap-3 mb-5 pb-3 border-b border-border">
                    <div className="w-7 h-7 rounded-full bg-accent text-accent-foreground flex items-center justify-center text-xs font-extrabold">
                      2
                    </div>
                    <div>
                      <h3 className="font-bold text-foreground">
                        Kendaraan & Paket
                      </h3>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Pilih kendaraan dan jenis layanan yang Anda butuhkan
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="sm:col-span-2">
                      <label className="form-label-style">
                        Kendaraan yang Diminati{' '}
                        <span className="text-red-500">*</span>
                      </label>

                      <div className="relative">
                        <input
                          type="hidden"
                          {...register('jenisKendaraan', {
                            required: 'Pilih jenis kendaraan',
                          })}
                          value={watchedValues.jenisKendaraan || ''}
                          readOnly
                        />

                        <button
                          type="button"
                          onClick={() => {
                            setVehicleDropdownOpen(!vehicleDropdownOpen);
                            setVehicleSearch('');
                          }}
                          className="form-input-style !pl-10 !pr-10 w-full text-left relative cursor-pointer"
                        >
                          <Car
                            size={16}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                          />

                          <span
                            className={
                              watchedValues.jenisKendaraan
                                ? 'text-foreground'
                                : 'text-muted-foreground'
                            }
                          >
                            {watchedValues.jenisKendaraan ||
                              '-- Pilih Kendaraan --'}
                          </span>

                          <ChevronDown
                            size={16}
                            className={`absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition-transform ${
                              vehicleDropdownOpen ? 'rotate-180' : ''
                            }`}
                          />
                        </button>

                        {vehicleDropdownOpen && (
                          <div className="absolute z-50 mt-2 w-full rounded-xl border border-border bg-card shadow-xl overflow-hidden">
                            <div className="p-3 border-b border-border bg-card">
                              <input
                                type="text"
                                value={vehicleSearch}
                                onChange={(e) =>
                                  setVehicleSearch(e.target.value)
                                }
                                placeholder="Cari kendaraan..."
                                autoFocus
                                className="form-input-style w-full"
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>

                            <div className="max-h-64 overflow-y-auto">
                              {filteredVehicleOptions.length > 0 ? (
                                filteredVehicleOptions.map((vehicle) => (
                                  <button
                                    key={`vehicle-${vehicle}`}
                                    type="button"
                                    onClick={() => {
                                      setValue('jenisKendaraan', vehicle, {
                                        shouldValidate: true,
                                        shouldDirty: true,
                                      });
                                      setVehicleDropdownOpen(false);
                                      setVehicleSearch('');
                                    }}
                                    className={`w-full text-left px-4 py-3 text-sm transition-colors hover:bg-accent/10 ${
                                      watchedValues.jenisKendaraan === vehicle
                                        ? 'bg-accent/10 text-foreground font-bold'
                                        : 'text-foreground'
                                    }`}
                                  >
                                    {vehicle}
                                  </button>
                                ))
                              ) : (
                                <div className="px-4 py-4 text-sm text-muted-foreground text-center">
                                  Kendaraan tidak ditemukan
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {errors.jenisKendaraan && (
                        <div className="flex items-center gap-1.5 mt-1">
                          <AlertCircle
                            size={12}
                            className="text-red-500"
                          />
                          <p className="form-error-style">
                            {errors.jenisKendaraan.message}
                          </p>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="form-label-style" htmlFor="paket">
                        Paket
                      </label>
                      <p className="text-xs text-muted-foreground mb-1.5">
                        Contoh: 12 Jam, 24 Jam, Dalam Kota, Luar Kota, dll.
                      </p>
                      <input
                        id="paket"
                        type="text"
                        placeholder="Contoh: Paket 12 Jam"
                        className="form-input-style"
                        {...register('paket')}
                      />
                    </div>

                    <div>
                      <label className="form-label-style">
                        Jenis Sewa <span className="text-red-500">*</span>
                      </label>

                      <div className="grid grid-cols-2 gap-3 mt-1">
                        {[
                          {
                            value: 'dengan-driver',
                            label: 'Dengan Driver',
                            desc: 'Driver profesional',
                          },
                          {
                            value: 'lepas-kunci',
                            label: 'Lepas Kunci',
                            desc: 'Anda mengemudi sendiri',
                          },
                        ].map((option) => (
                          <label
                            key={option.value}
                            className={`flex items-start gap-2 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                              watchedValues.jenisSewa === option.value
                                ? 'border-accent bg-accent/5'
                                : 'border-border hover:border-accent/40'
                            }`}
                          >
                            <input
                              type="radio"
                              value={option.value}
                              className="mt-1 accent-amber-600"
                              {...register('jenisSewa', {
                                required: 'Pilih jenis sewa',
                              })}
                            />
                            <div>
                              <div className="font-bold text-foreground text-xs flex items-center gap-1.5">
                                <UserCheck
                                  size={13}
                                  className="text-accent"
                                />
                                {option.label}
                              </div>
                              <div className="text-muted-foreground text-[11px] mt-0.5">
                                {option.desc}
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>

                      {errors.jenisSewa && (
                        <div className="flex items-center gap-1.5 mt-1">
                          <AlertCircle
                            size={12}
                            className="text-red-500"
                          />
                          <p className="form-error-style">
                            {errors.jenisSewa.message}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* SECTION 3 */}
                <div className="mb-8">
                  <div className="flex items-center gap-3 mb-5 pb-3 border-b border-border">
                    <div className="w-7 h-7 rounded-full bg-accent text-accent-foreground flex items-center justify-center text-xs font-extrabold">
                      3
                    </div>
                    <div>
                      <h3 className="font-bold text-foreground">
                        Waktu & Perjalanan
                      </h3>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Durasi dihitung berdasarkan jumlah tanggal, bukan jam
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label
                        className="form-label-style"
                        htmlFor="tanggalMulai"
                      >
                        Tanggal Penggunaan{' '}
                        <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Calendar
                          size={16}
                          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                        />
                        <input
                          id="tanggalMulai"
                          type="date"
                          className="form-input-style !pl-10"
                          {...register('tanggalMulai', {
                            required: 'Tanggal penggunaan wajib diisi',
                            validate: (value) => {
                              const today = new Date();
                              today.setHours(0, 0, 0, 0);
                              return (
                                getDateOnly(value) >= today ||
                                'Tanggal tidak boleh di masa lalu'
                              );
                            },
                          })}
                        />
                      </div>
                      {errors.tanggalMulai && (
                        <div className="flex items-center gap-1.5 mt-1">
                          <AlertCircle
                            size={12}
                            className="text-red-500"
                          />
                          <p className="form-error-style">
                            {errors.tanggalMulai.message}
                          </p>
                        </div>
                      )}
                    </div>

                    <div>
                      <label
                        className="form-label-style"
                        htmlFor="jamPenjemputan"
                      >
                        Jam Penjemputan{' '}
                        <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Clock
                          size={16}
                          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                        />
                        <input
                          id="jamPenjemputan"
                          type="time"
                          className="form-input-style !pl-10"
                          {...register('jamPenjemputan', {
                            required: 'Jam penjemputan wajib diisi',
                          })}
                        />
                      </div>
                      {errors.jamPenjemputan && (
                        <div className="flex items-center gap-1.5 mt-1">
                          <AlertCircle
                            size={12}
                            className="text-red-500"
                          />
                          <p className="form-error-style">
                            {errors.jamPenjemputan.message}
                          </p>
                        </div>
                      )}
                    </div>

                    <div>
                      <label
                        className="form-label-style"
                        htmlFor="tanggalSelesai"
                      >
                        Tanggal Selesai <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Calendar
                          size={16}
                          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                        />
                        <input
                          id="tanggalSelesai"
                          type="date"
                          className="form-input-style !pl-10"
                          {...register('tanggalSelesai', {
                            required: 'Tanggal selesai wajib diisi',
                            validate: (value) => {
                              if (!watchedValues.tanggalMulai) return true;

                              return (
                                getDateOnly(value) >=
                                  getDateOnly(watchedValues.tanggalMulai) ||
                                'Tanggal selesai harus sama atau setelah tanggal penggunaan'
                              );
                            },
                          })}
                        />
                      </div>
                      {errors.tanggalSelesai && (
                        <div className="flex items-center gap-1.5 mt-1">
                          <AlertCircle
                            size={12}
                            className="text-red-500"
                          />
                          <p className="form-error-style">
                            {errors.tanggalSelesai.message}
                          </p>
                        </div>
                      )}
                    </div>

                    <div>
                      <label
                        className="form-label-style"
                        htmlFor="jamSelesai"
                      >
                        Jam Selesai / Finish{' '}
                        <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Clock
                          size={16}
                          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                        />
                        <input
                          id="jamSelesai"
                          type="time"
                          className="form-input-style !pl-10"
                          {...register('jamSelesai', {
                            required: 'Jam selesai wajib diisi',
                          })}
                        />
                      </div>
                      {errors.jamSelesai && (
                        <div className="flex items-center gap-1.5 mt-1">
                          <AlertCircle
                            size={12}
                            className="text-red-500"
                          />
                          <p className="form-error-style">
                            {errors.jamSelesai.message}
                          </p>
                        </div>
                      )}
                    </div>

                    <div>
                      <label
                        className="form-label-style"
                        htmlFor="lokasiPenjemputan"
                      >
                        Lokasi Penjemputan{' '}
                        <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <MapPin
                          size={16}
                          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                        />
                        <input
                          id="lokasiPenjemputan"
                          type="text"
                          placeholder="Contoh: Bandara Soekarno-Hatta"
                          className="form-input-style !pl-10"
                          {...register('lokasiPenjemputan', {
                            required: 'Lokasi penjemputan wajib diisi',
                          })}
                        />
                      </div>
                      {errors.lokasiPenjemputan && (
                        <div className="flex items-center gap-1.5 mt-1">
                          <AlertCircle
                            size={12}
                            className="text-red-500"
                          />
                          <p className="form-error-style">
                            {errors.lokasiPenjemputan.message}
                          </p>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="form-label-style" htmlFor="tujuan">
                        Tujuan / Rute <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <MapPin
                          size={16}
                          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                        />
                        <input
                          id="tujuan"
                          type="text"
                          placeholder="Contoh: Jakarta – Bandung"
                          className="form-input-style !pl-10"
                          {...register('tujuan', {
                            required: 'Tujuan / rute wajib diisi',
                          })}
                        />
                      </div>
                      {errors.tujuan && (
                        <div className="flex items-center gap-1.5 mt-1">
                          <AlertCircle
                            size={12}
                            className="text-red-500"
                          />
                          <p className="form-error-style">
                            {errors.tujuan.message}
                          </p>
                        </div>
                      )}
                    </div>

                    <div>
                      <label
                        className="form-label-style"
                        htmlFor="lokasiFinish"
                      >
                        Lokasi Finish
                      </label>
                      <div className="relative">
                        <MapPin
                          size={16}
                          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                        />
                        <input
                          id="lokasiFinish"
                          type="text"
                          placeholder="Contoh: Hotel / rumah / kantor"
                          className="form-input-style !pl-10"
                          {...register('lokasiFinish')}
                        />
                      </div>
                    </div>

                    <div>
                      <label
                        className="form-label-style"
                        htmlFor="jumlahPenumpang"
                      >
                        Jumlah Penumpang{' '}
                        <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Users
                          size={16}
                          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                        />
                        <input
                          id="jumlahPenumpang"
                          type="number"
                          min="1"
                          max="59"
                          placeholder="Contoh: 4"
                          className="form-input-style !pl-10"
                          {...register('jumlahPenumpang', {
                            required: 'Jumlah penumpang wajib diisi',
                            min: {
                              value: 1,
                              message: 'Minimal 1 penumpang',
                            },
                            max: {
                              value: 59,
                              message: 'Maksimal 59 penumpang',
                            },
                          })}
                        />
                      </div>
                      {errors.jumlahPenumpang && (
                        <div className="flex items-center gap-1.5 mt-1">
                          <AlertCircle
                            size={12}
                            className="text-red-500"
                          />
                          <p className="form-error-style">
                            {errors.jumlahPenumpang.message}
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="sm:col-span-2">
                      <div className="rounded-xl border border-accent/30 bg-accent/5 p-4">
                        <div className="text-xs text-muted-foreground mb-1">
                          Durasi Pemakaian
                        </div>
                        <div className="text-xl font-extrabold text-foreground">
                          {calculateDuration(
                            watchedValues.tanggalMulai || '',
                            watchedValues.tanggalSelesai || ''
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          Dihitung dari jumlah tanggal. Jam penjemputan dan jam
                          finish tidak mengubah jumlah hari.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* SECTION 4 */}
                <div className="mb-8">
                  <div className="flex items-center gap-3 mb-5 pb-3 border-b border-border">
                    <div className="w-7 h-7 rounded-full bg-accent text-accent-foreground flex items-center justify-center text-xs font-extrabold">
                      4
                    </div>
                    <div>
                      <h3 className="font-bold text-foreground">
                        Rute Perjalanan
                      </h3>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Isi jika perjalanan lebih dari satu hari
                      </p>
                    </div>
                  </div>

                  <div className="space-y-5">
                    <div>
                      <label
                        className="form-label-style"
                        htmlFor="rutePerjalanan"
                      >
                        Rute Perjalanan
                      </label>
                      <textarea
                        id="rutePerjalanan"
                        rows={3}
                        placeholder="Contoh: Jakarta → Bandung → Lembang → Jakarta"
                        className="form-input-style resize-none"
                        {...register('rutePerjalanan')}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                      <div>
                        <label className="form-label-style" htmlFor="day1">
                          Day 1
                        </label>
                        <textarea
                          id="day1"
                          rows={5}
                          placeholder="Contoh: Jemput di Soetta → Hotel Bandung → makan malam"
                          className="form-input-style resize-none"
                          {...register('day1')}
                        />
                      </div>

                      <div>
                        <label className="form-label-style" htmlFor="day2">
                          Day 2
                        </label>
                        <textarea
                          id="day2"
                          rows={5}
                          placeholder="Contoh: Hotel → Kawah Putih → Ciwidey → hotel"
                          className="form-input-style resize-none"
                          {...register('day2')}
                        />
                      </div>

                      <div>
                        <label className="form-label-style" htmlFor="day3">
                          Day 3
                        </label>
                        <textarea
                          id="day3"
                          rows={5}
                          placeholder="Contoh: Hotel → wisata → Jakarta → finish"
                          className="form-input-style resize-none"
                          {...register('day3')}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* SECTION 5 */}
                <div className="mb-8">
                  <div className="flex items-center gap-3 mb-5 pb-3 border-b border-border">
                    <div className="w-7 h-7 rounded-full bg-accent text-accent-foreground flex items-center justify-center text-xs font-extrabold">
                      5
                    </div>
                    <div>
                      <h3 className="font-bold text-foreground">
                        Catatan Tambahan
                      </h3>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Sampaikan kebutuhan khusus Anda
                      </p>
                    </div>
                  </div>

                  <div>
                    <label
                      className="form-label-style"
                      htmlFor="catatan"
                    >
                      Catatan / Permintaan Khusus
                    </label>
                    <p className="text-xs text-muted-foreground mb-1.5">
                      Opsional — misalnya permintaan kursi bayi, kendaraan
                      datang lebih awal, kebutuhan acara, atau detail lainnya.
                    </p>

                    <div className="relative">
                      <FileText
                        size={16}
                        className="absolute left-3 top-3.5 text-muted-foreground"
                      />
                      <textarea
                        id="catatan"
                        rows={5}
                        placeholder="Tulis catatan atau permintaan khusus Anda..."
                        className="form-input-style !pl-10 resize-none"
                        {...register('catatan')}
                      />
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full btn-primary py-4 rounded-xl text-base font-bold flex items-center justify-center gap-3 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {isSubmitting ? (
                    <>
                      <svg
                        className="animate-spin w-5 h-5"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        />
                      </svg>
                      Memproses...
                    </>
                  ) : (
                    <>
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.149.198-.298.198-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                      </svg>
                      LANJUT KE RINGKASAN BOOKING
                    </>
                  )}
                </button>

                <p className="text-center text-muted-foreground text-xs mt-4">
                  Dengan mengisi form ini, Anda menyetujui untuk dihubungi
                  oleh tim NAYVERA INDO TRANSPORT via WhatsApp.
                </p>
              </form>
            </div>
          </div>

          {/* SIDEBAR */}
          <div className="lg:col-span-1 space-y-5 lg:sticky lg:top-28">
            <div className="summary-card p-6">
              <div className="text-accent text-xs font-bold uppercase tracking-widest mb-4">
                Ringkasan Booking
              </div>

              <div className="space-y-3">
                {[
                  {
                    label: 'Nama Pemesan',
                    value: watchedValues.namaPemesan || '—',
                    empty: !watchedValues.namaPemesan,
                  },
                  {
                    label: 'Nama Tamu',
                    value: watchedValues.namaTamu || '—',
                    empty: !watchedValues.namaTamu,
                  },
                  {
                    label: 'WhatsApp',
                    value: watchedValues.nomorWhatsapp || '—',
                    empty: !watchedValues.nomorWhatsapp,
                  },
                  {
                    label: 'Kendaraan',
                    value: watchedValues.jenisKendaraan || '—',
                    empty: !watchedValues.jenisKendaraan,
                  },
                  {
                    label: 'Paket',
                    value: watchedValues.paket || '—',
                    empty: !watchedValues.paket,
                  },
                  {
                    label: 'Jenis Sewa',
                    value:
                      watchedValues.jenisSewa === 'dengan-driver'
                        ? 'Dengan Driver'
                        : watchedValues.jenisSewa === 'lepas-kunci'
                          ? 'Lepas Kunci'
                          : '—',
                    empty: !watchedValues.jenisSewa,
                  },
                  {
                    label: 'Tanggal Penggunaan',
                    value: watchedValues.tanggalMulai
                      ? formatDate(watchedValues.tanggalMulai)
                      : '—',
                    empty: !watchedValues.tanggalMulai,
                  },
                  {
                    label: 'Jam Penjemputan',
                    value: watchedValues.jamPenjemputan || '—',
                    empty: !watchedValues.jamPenjemputan,
                  },
                  {
                    label: 'Tanggal Selesai',
                    value: watchedValues.tanggalSelesai
                      ? formatDate(watchedValues.tanggalSelesai)
                      : '—',
                    empty: !watchedValues.tanggalSelesai,
                  },
                  {
                    label: 'Jam Finish',
                    value: watchedValues.jamSelesai || '—',
                    empty: !watchedValues.jamSelesai,
                  },
                  {
                    label: 'Durasi',
                    value: calculateDuration(
                      watchedValues.tanggalMulai || '',
                      watchedValues.tanggalSelesai || ''
                    ),
                    empty:
                      !watchedValues.tanggalMulai ||
                      !watchedValues.tanggalSelesai,
                  },
                  {
                    label: 'Penumpang',
                    value: watchedValues.jumlahPenumpang
                      ? `${watchedValues.jumlahPenumpang} orang`
                      : '—',
                    empty: !watchedValues.jumlahPenumpang,
                  },
                  {
                    label: 'Penjemputan',
                    value: watchedValues.lokasiPenjemputan || '—',
                    empty: !watchedValues.lokasiPenjemputan,
                  },
                  {
                    label: 'Tujuan / Rute',
                    value: watchedValues.tujuan || '—',
                    empty: !watchedValues.tujuan,
                  },
                  {
                    label: 'Lokasi Finish',
                    value: watchedValues.lokasiFinish || '—',
                    empty: !watchedValues.lokasiFinish,
                  },
                ].map((item) => (
                  <div
                    key={`live-${item.label}`}
                    className="flex justify-between gap-3 text-sm"
                  >
                    <span className="text-white/45 shrink-0">
                      {item.label}
                    </span>
                    <span
                      className={`font-semibold text-right max-w-[190px] ${
                        item.empty
                          ? 'text-white/25 italic'
                          : 'text-white'
                      }`}
                    >
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>

              <div className="mt-5 pt-4 border-t border-white/10">
                <div className="text-white/45 text-xs mb-1">
                  Harga akan dikonfirmasi via
                </div>

                <div className="flex items-center gap-2 text-green-400 font-bold text-sm">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.198.198-.298.198-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                  WhatsApp
                </div>
              </div>
            </div>

            <div className="bg-card rounded-2xl border border-border p-5">
              <h4 className="font-bold text-foreground text-sm mb-3">
                Informasi Penting
              </h4>

              <ul className="space-y-2.5">
                {[
                  'Harga akan dikonfirmasi setelah detail perjalanan diperiksa.',
                  'Tim NAYVERA akan menghubungi Anda melalui WhatsApp.',
                  'Silakan isi Day 1–Day 3 jika perjalanan multi-hari.',
                  'Durasi dihitung berdasarkan jumlah tanggal.',
                ].map((info, index) => (
                  <li
                    key={`info-${index}`}
                    className="flex gap-2 items-start text-xs text-muted-foreground"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-accent shrink-0 mt-1.5" />
                    {info}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-2xl p-5">
              <h4 className="font-bold text-green-800 text-sm mb-1">
                Butuh Bantuan?
              </h4>

              <p className="text-green-700 text-xs mb-3">
                Hubungi kami langsung via WhatsApp untuk konsultasi gratis.
              </p>

              <a
                href={`https://wa.me/${WA_NUMBER}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-2.5 rounded-lg bg-green-500 hover:bg-green-600 text-white text-sm font-bold transition-colors"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="white"
                >
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.198.198-.298.198-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.149-.347-.223-.644-.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.198-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                Chat dengan Admin
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}